import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MISSION_CREW, OTHER_PLAYERS, CREW_CHAT_SEED, MISSION_ROLES,
  CONTRIBUTIONS, MISSION_TARGET, MISSION_SCORE, IMAGES,
} from '../mock/data';
import { useDemo } from '../context/DemoContext';
import { StatusDot } from '../components/xc/Bits';
import { InvestorNote } from '../components/xc/InvestorNote';
import CrewChat from '../components/xc/CrewChat';
import SlotDemo from '../components/xc/SlotDemo';
import { QUALIFYING_SLOTS } from '../mock/data';
import * as Icons from 'lucide-react';
import {
  Radio, Users, Coins, Gauge, DoorOpen, LogOut, Check, ChevronRight,
  AlertTriangle, ShieldAlert, Trophy, Car, Info, ArrowRight, X, UserRound,
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const STEPS = ['Intro', 'Assembly', 'Approach', 'Roles', 'Contribution', 'Result'];

function Ghost({ children }) {
  return (
    <div className="rounded-xl border border-[#FF204E]/30 overflow-hidden">
      <div className="px-4 py-2 flex items-center gap-2" style={{ background: 'linear-gradient(90deg,rgba(217,0,53,0.25),rgba(122,24,255,0.15))' }}>
        <Radio className="h-3.5 w-3.5 text-[#FF204E] xc-pulse" />
        <span className="text-[11px] font-700 tracking-[0.2em] text-[#FF204E]">GHOST — MISSION CONTROL</span>
      </div>
      <div className="px-4 py-4 bg-black/30 text-white/85 text-sm leading-relaxed italic">{children}</div>
    </div>
  );
}

function Stepper({ idx }) {
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-nowrap pb-1">
      {STEPS.map((s, i) => (
        <React.Fragment key={s}>
          <span className={`whitespace-nowrap text-[11px] font-500 px-2.5 py-1 rounded-full border ${i === idx ? 'text-white border-[#FF204E]/50 bg-[#FF204E]/10' : i < idx ? 'text-[#2FBF71] border-[#2FBF71]/30' : 'text-white/35 border-white/10'}`}>
            {i < idx ? '✓ ' : ''}{s}
          </span>
          {i < STEPS.length - 1 && <span className="h-px w-3 bg-white/10" />}
        </React.Fragment>
      ))}
    </div>
  );
}

function PlayerCard({ p, selectable }) {
  const available = ['Available', 'Ready', 'Leader'].includes(p.status);
  const dim = selectable && !available;
  return (
    <div className={`rounded-xl p-3.5 border flex items-center gap-3 ${dim ? 'border-white/5 opacity-45' : 'border-white/10 bg-white/[0.03]'}`}>
      <span className="h-10 w-10 rounded-full grid place-items-center text-sm font-600 text-white flex-shrink-0" style={{ background: p.role === 'Leader' ? 'linear-gradient(135deg,#E0B85A,#D6A84B)' : 'linear-gradient(135deg,#2a2140,#151119)', color: p.role === 'Leader' ? '#1a1206' : '#fff', border: '1px solid rgba(255,255,255,0.12)' }}>{p.name[0]}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2"><span className="font-500 text-white text-sm">{p.name}</span>{p.role === 'Leader' && <span className="text-[9px] px-1.5 py-0.5 rounded text-[#E0B85A] border border-[#E0B85A]/40">LEADER</span>}</div>
        <div className="flex items-center gap-1.5 text-xs text-white/50 mt-0.5"><StatusDot status={p.status} /> {p.status} · Lvl {p.level}</div>
      </div>
      {selectable && available && <Check className="h-4 w-4 text-[#2FBF71]" />}
    </div>
  );
}

function VoteRow({ name, choice, color }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
      <span className="text-sm text-white/75">{name}</span>
      <span className="text-sm font-500 flex items-center gap-1.5" style={{ color }}><ArrowRight className="h-3.5 w-3.5" />{choice}</span>
    </div>
  );
}

export default function LoyaltyJob({ solo = false }) {
  const nav = useNavigate();
  const { setLoyaltyJobStatus, addCoins, ownItem } = useDemo();
  const [step, setStep] = useState(0);
  const [mid, setMid] = useState(null);
  const [outcome, setOutcome] = useState('success');
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [rewarded, setRewarded] = useState(false);
  const [activeSlot, setActiveSlot] = useState(null);
  const [bonusRounds, setBonusRounds] = useState(0);

  const cfg = solo ? {
    eyebrow: 'Downtown · Solo Operation',
    title: 'THE MIDNIGHT RUN',
    ghostIntro: "A private courier is moving through Downtown tonight. No crew, no votes — this one is just for you.",
    crewRequired: '1', rewardLabel: '800 LC', difficulty: 'Medium',
    rewardCoins: { success: 800, fail: 300 }, rewardRep: { success: 150, fail: 50 },
    scoreLabel: 'MISSION SCORE', target: 3200, base: 2860,
    successMsg: 'You slipped through Downtown unseen and vanished into the rain. Clean work, operator.',
    unlockLabel: 'Nightrunner Plates', unlockItem: null,
  } : {
    eyebrow: 'Downtown · High Difficulty',
    title: 'THE LOYALTY JOB',
    ghostIntro: 'Loyalty Bank Inc. is moving something valuable through Downtown tonight. Four people are required.',
    crewRequired: '4', rewardLabel: '1,500 LC each', difficulty: 'High',
    rewardCoins: { success: 1500, fail: 500 }, rewardRep: { success: 400, fail: 100 },
    scoreLabel: 'CREW SCORE', target: MISSION_TARGET, base: MISSION_SCORE,
    successMsg: 'The crew slipped out of Downtown before the lockdown completed. The city never saw their faces.',
    unlockLabel: 'Loyalty Bank Escape Car', unlockItem: 'loyalty-escape-car',
  };

  const leave = () => {
    setLeaveOpen(false);
    setLoyaltyJobStatus('left');
    nav('/casino');
  };

  const goResult = (result) => {
    setOutcome(result);
    setStep(5);
    if (!rewarded) {
      setRewarded(true);
      addCoins(result === 'success' ? cfg.rewardCoins.success : cfg.rewardCoins.fail);
      if (result === 'success' && cfg.unlockItem) ownItem(cfg.unlockItem);
      setLoyaltyJobStatus(result);
    }
  };

  const cappedBonus = Math.min(bonusRounds, 8);
  const yordyRounds = 12 + cappedBonus;
  const yordyPoints = 2860 + cappedBonus * 45;
  const midBonus = mid === 'push' ? 340 : mid === 'tunnel' ? 120 : 0;
  const scoreFinal = cfg.base + cappedBonus * 45 + midBonus;
  const targetHit = scoreFinal >= cfg.target;
  const playRound = () => setBonusRounds((b) => Math.min(8, b + 1));
  const chooseNoun = solo ? 'You chose this' : 'Crew chose this';

  return (
    <div className="relative min-h-screen bg-[#070609]">
      {/* Cinematic backdrop */}
      <div className="absolute inset-0 h-[520px] overflow-hidden">
        <img src={IMAGES.alleyCar} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 city-overlay" />
        <div className="absolute inset-0 vignette" />
      </div>

      <div className="relative max-w-[1180px] mx-auto px-4 lg:px-8 py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs tracking-[0.24em] uppercase text-[#FF204E] mb-2">{cfg.eyebrow}</div>
            <h1 className="font-display font-700 text-3xl sm:text-4xl md:text-5xl text-white tracking-tight">{cfg.title}</h1>
          </div>
          {step > 0 && (
            <button onClick={() => setLeaveOpen(true)} className="btn-ghost rounded-lg px-4 py-2.5 text-sm font-500 flex items-center gap-2"><LogOut className="h-4 w-4" /> Leave Mission</button>
          )}
        </div>
        <div className="mt-6"><Stepper idx={step} /></div>
        <div className="mt-4"><InvestorNote id="loyalty-job" title="Why contribution is capped">
          Each member's qualifying rounds are hard-capped, so a mission can never pressure anyone into playing more than intended. The mission needs the crew, not the volume — responsible gambling is designed into the mechanic, not bolted on.
        </InvestorNote></div>

        <div className="mt-8">
          {/* STEP 0 — INTRO */}
          {step === 0 && (
            <div className="grid lg:grid-cols-[1fr_360px] gap-6 xc-fade-up">
              <div className="space-y-5">
                <Ghost>{cfg.ghostIntro}</Ghost>
                <div className="xc-glass rounded-2xl p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[{ i: Users, l: solo ? 'Operator' : 'Crew Required', v: cfg.crewRequired }, { i: Gauge, l: 'Max Contribution', v: '20 rounds' }, { i: Coins, l: 'Potential Reward', v: cfg.rewardLabel }, { i: ShieldAlert, l: 'Difficulty', v: cfg.difficulty }].map((x, k) => (
                    <div key={k}>
                      <x.i className="h-5 w-5 text-[#B026FF] mb-2" />
                      <div className="text-[11px] text-white/40 uppercase tracking-wide">{x.l}</div>
                      <div className="font-display font-600 text-white">{x.v}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => setStep(1)} className="btn-neon rounded-xl px-7 py-3.5 font-700 tracking-wide flex items-center gap-2">I'M IN <ArrowRight className="h-4 w-4" /></button>
                  <button onClick={() => nav('/casino')} className="btn-ghost rounded-xl px-7 py-3.5 font-600">NOT NOW</button>
                </div>
                <p className="text-xs text-white/40 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Ghost is your narrator and mission controller — not another player. Declining never penalizes you.</p>
              </div>
              <div className="rounded-2xl overflow-hidden xc-glass border border-[#B026FF]/20">
                <img src={IMAGES.car} alt="Getaway car" className="h-40 w-full object-cover" />
                <div className="p-5">
                  <div className="neon-gold font-display font-700 text-lg">LOYALTY BANK INC.</div>
                  <p className="text-sm text-white/55 mt-2 leading-relaxed">A black getaway car idles on wet pavement. Rain streaks the neon. The city is watching.</p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 1 — ASSEMBLY */}
          {step === 1 && !solo && (
            <div className="grid lg:grid-cols-[1fr_360px] gap-6 xc-fade-up">
              <div className="space-y-6">
                <div>
                  <h2 className="font-display font-700 text-xl text-white mb-3">Crew Assembled <span className="text-white/40 text-sm font-400">— 4 / 4</span></h2>
                  <div className="grid sm:grid-cols-2 gap-3">{MISSION_CREW.map((p) => <PlayerCard key={p.id} p={p} />)}</div>
                </div>
                <div>
                  <h3 className="text-sm tracking-[0.16em] uppercase text-white/45 mb-3">Other Players</h3>
                  <div className="grid sm:grid-cols-2 gap-3">{OTHER_PLAYERS.map((p) => <PlayerCard key={p.id} p={p} selectable />)}</div>
                  <p className="text-xs text-white/40 mt-3 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Players marked Casino Only, Do Not Disturb or Offline are never shown as available mission candidates.</p>
                </div>
                <button onClick={() => setStep(2)} className="btn-neon rounded-xl px-7 py-3.5 font-700 flex items-center gap-2">CONTINUE <ArrowRight className="h-4 w-4" /></button>
              </div>
              <CrewChat seed={CREW_CHAT_SEED} className="h-fit" />
            </div>
          )}

          {/* STEP 1 — SOLO BRIEFING */}
          {step === 1 && solo && (
            <div className="grid lg:grid-cols-[1fr_360px] gap-6 xc-fade-up">
              <div className="space-y-6">
                <div>
                  <h2 className="font-display font-700 text-xl text-white mb-3">Flying Solo <span className="text-white/40 text-sm font-400">— 1 / 1</span></h2>
                  <div className="grid sm:grid-cols-2 gap-3">{MISSION_CREW.filter((p) => p.name === 'Yordy').map((p) => <PlayerCard key={p.id} p={p} />)}</div>
                </div>
                <div className="rounded-2xl p-5 border border-[#B026FF]/20 bg-white/[0.03]">
                  <div className="flex items-center gap-2 mb-2"><UserRound className="h-4 w-4 text-[#B026FF]" /><span className="text-sm tracking-[0.16em] uppercase text-white/60">No crew required</span></div>
                  <p className="text-sm text-white/55 leading-relaxed">Solo missions run the exact same way — briefing, approach, roles and a capped contribution — but there are no other players, no votes and no crew chat. Every decision is yours alone.</p>
                </div>
                <button onClick={() => setStep(2)} className="btn-neon rounded-xl px-7 py-3.5 font-700 flex items-center gap-2">CONTINUE <ArrowRight className="h-4 w-4" /></button>
              </div>
              <div className="rounded-2xl xc-glass border border-[#FF204E]/20 overflow-hidden h-fit">
                <div className="px-4 py-2 flex items-center gap-2" style={{ background: 'linear-gradient(90deg,rgba(217,0,53,0.25),rgba(122,24,255,0.15))' }}>
                  <Radio className="h-3.5 w-3.5 text-[#FF204E] xc-pulse" />
                  <span className="text-[11px] font-700 tracking-[0.2em] text-[#FF204E]">GHOST — MISSION CONTROL</span>
                </div>
                <div className="p-4 space-y-3 text-sm">
                  <p className="text-white/80 italic">Comms are open. It's just you and me tonight, operator.</p>
                  <p className="text-white/60 italic">Keep it quiet, keep it clean. I'll call the changes as the city reacts.</p>
                  <p className="text-white/45 text-xs pt-2 border-t border-white/8">Ghost is your narrator and mission controller — never another player.</p>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — APPROACH VOTE */}
          {step === 2 && (
            <div className="max-w-3xl xc-fade-up space-y-5">
              <Ghost>Security changed the patrol route. Choose your approach.</Ghost>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="xc-card rounded-2xl p-6 border-[#FF204E]/40">
                  <DoorOpen className="h-6 w-6 text-[#FF204E] mb-3" />
                  <h3 className="font-display font-700 text-white text-lg">MAIN ENTRANCE</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-white/60"><li>↑ Higher difficulty</li><li className="text-[#E0B85A]">↑ Higher Xincity reward</li></ul>
                </div>
                <div className="xc-card rounded-2xl p-6">
                  <Car className="h-6 w-6 text-[#B026FF] mb-3" />
                  <h3 className="font-display font-700 text-white text-lg">UNDERGROUND GARAGE</h3>
                  <ul className="mt-3 space-y-1.5 text-sm text-white/60"><li>↓ Lower difficulty</li><li>↓ Lower Xincity reward</li></ul>
                </div>
              </div>
              <div className="xc-glass rounded-2xl p-5">
                <div className="text-xs tracking-[0.16em] uppercase text-white/45 mb-2">{solo ? 'Your Decision' : 'Crew Votes'}</div>
                {solo ? (
                  <VoteRow name="Yordy" choice="Main Entrance" color="#FF204E" />
                ) : (
                  <>
                    <VoteRow name="Yordy" choice="Main Entrance" color="#FF204E" />
                    <VoteRow name="Anna88" choice="Main Entrance" color="#FF204E" />
                    <VoteRow name="Marco" choice="Underground Garage" color="#B026FF" />
                    <VoteRow name="Wolf" choice="Main Entrance" color="#FF204E" />
                  </>
                )}
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                  <span className="text-sm text-white/50">Result</span>
                  <span className="font-display font-700 neon-red">{solo ? 'YOUR APPROACH: MAIN ENTRANCE' : 'MAJORITY: MAIN ENTRANCE'}</span>
                </div>
              </div>
              <button onClick={() => setStep(3)} className="btn-neon rounded-xl px-7 py-3.5 font-700 flex items-center gap-2">ASSIGN ROLES <ArrowRight className="h-4 w-4" /></button>
            </div>
          )}

          {/* STEP 3 — ROLES */}
          {step === 3 && (
            <div className="max-w-4xl xc-fade-up space-y-5">
              <h2 className="font-display font-700 text-xl text-white">Mission Roles</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {MISSION_ROLES.map((r) => {
                  const Icon = Icons[r.icon] || Icons.User;
                  return (
                    <div key={r.role} className="xc-card rounded-2xl p-5">
                      <div className="h-11 w-11 rounded-xl grid place-items-center mb-4" style={{ background: '#B026FF16', border: '1px solid #B026FF44' }}><Icon className="h-5 w-5 text-[#B026FF]" /></div>
                      <div className="font-display font-700 text-white">{r.role}</div>
                      <div className="text-xs text-[#E0B85A] mt-0.5">{solo ? 'Yordy' : r.player}</div>
                      <p className="text-sm text-white/55 mt-2 leading-relaxed">{r.effect}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-white/40 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> {solo ? 'In a solo mission you take on every role yourself. Roles affect only Xincity — never the provider casino game.' : 'Roles affect only Xincity. They never modify the provider casino game.'}</p>
              <button onClick={() => setStep(4)} className="btn-neon rounded-xl px-7 py-3.5 font-700 flex items-center gap-2">BEGIN CONTRIBUTION <ArrowRight className="h-4 w-4" /></button>
            </div>
          )}

          {/* STEP 4 — CONTRIBUTION + MID DECISION */}
          {step === 4 && (
            <div className="space-y-6 xc-fade-up">
              <div>
                <h2 className="font-display font-700 text-2xl text-white">{solo ? 'YOU PLAY YOUR OWN CASINO GAME' : 'EVERY PLAYER PLAYS THEIR OWN CASINO GAME'}</h2>
                <p className="text-white/55 mt-2 max-w-2xl">{solo ? 'You contribute by playing normally. Xincity simply reacts to your trusted settled rounds — capped, so more spending never buys more mission value.' : 'Each crew member contributes by playing normally. Xincity simply reacts to their trusted settled rounds.'}</p>
              </div>
              <div className={`grid gap-4 ${solo ? 'sm:grid-cols-1 max-w-sm' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
                {(solo ? CONTRIBUTIONS.filter((c) => c.name === 'Yordy') : CONTRIBUTIONS).map((c) => {
                  const isY = c.name === 'Yordy';
                  const rnds = isY ? yordyRounds : c.rounds;
                  const pts = isY ? yordyPoints : c.points;
                  return (
                    <div key={c.name} className={`xc-card rounded-2xl overflow-hidden ${isY ? 'ring-1 ring-[#FF204E]/40' : ''}`}>
                      <div className="h-24 relative" style={{ background: c.grad }}>
                        <div className="absolute inset-0 xc-grid opacity-20" />
                        <div className="absolute bottom-2 left-3 font-display font-700 text-white flex items-center gap-1.5">{c.name}{isY && <span className="text-[9px] px-1.5 py-0.5 rounded bg-black/40 text-white/80">YOU</span>}</div>
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between text-sm"><span className="text-white/50">Rounds</span><span className="text-white font-600">{rnds} / 20</span></div>
                        <div className="h-1.5 rounded-full bg-white/10 mt-2 overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${(rnds / 20) * 100}%`, background: 'linear-gradient(90deg,#FF204E,#B026FF)' }} /></div>
                        <div className="mt-3 text-[#E0B85A] font-600">{pts.toLocaleString()} <span className="text-xs text-white/40 font-400">Mission Points</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="xc-glass rounded-2xl p-6">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-display font-700 text-white">{cfg.scoreLabel}</span>
                  <span className="font-display font-700 text-2xl"><span className="neon-gold">{scoreFinal.toLocaleString()}</span> <span className="text-white/40 text-lg">/ {cfg.target.toLocaleString()}</span></span>
                </div>
                <div className="h-3 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${Math.min(100, (scoreFinal / cfg.target) * 100)}%`, background: 'linear-gradient(90deg,#D90035,#B026FF,#E0B85A)' }} /></div>
              </div>

              <div className="rounded-xl p-4 bg-white/[0.03] border border-white/8 text-sm text-white/55 leading-relaxed">
                <Info className="h-4 w-4 text-[#B026FF] inline mr-2 -mt-0.5" />
                {solo
                  ? 'In production, you play your own casino game. Xincity receives trusted settled-round events from the casino/provider platform and never alters reels, RNG or outcomes. Contributions are capped — bigger stakes do not create more mission value, and no one keeps gambling to force completion.'
                  : 'In production, every player plays their own casino game. Xincity receives trusted settled-round events from the casino/provider platform and never alters reels, RNG or outcomes. Contributions are capped — bigger stakes do not create more mission value, and no one keeps gambling to force completion.'}
              </div>

              {/* Qualifying slots — playable inline demo */}
              <div className="xc-glass rounded-2xl p-6">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
                  <div>
                    <h3 className="font-display font-700 text-white">PLAY A QUALIFYING ROUND</h3>
                    <p className="text-sm text-white/50 mt-0.5">Pick a slot and play a real demo round — your settled rounds feed {solo ? 'your mission score' : 'the crew score'}.</p>
                  </div>
                  <span className="text-sm px-3 py-1.5 rounded-lg border border-[#FF204E]/30 bg-[#FF204E]/8 text-white/85">Yordy: <span className="font-600">{yordyRounds} / 20</span> rounds</span>
                </div>

                {activeSlot ? (
                  <SlotDemo slot={activeSlot} onClose={() => setActiveSlot(null)} onRound={playRound} capped={yordyRounds >= 20} />
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {QUALIFYING_SLOTS.map((s) => {
                      const SlotIcon = Icons[s.icon] || Icons.Dice5;
                      return (
                        <button key={s.id} onClick={() => setActiveSlot(s)} className="group relative rounded-xl overflow-hidden aspect-[3/4] xc-card">
                          <div className="absolute inset-0" style={{ background: s.grad }} />
                          <div className="absolute inset-0 xc-grid opacity-20" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                          <div className="absolute inset-0 grid place-items-center"><SlotIcon className="h-8 w-8 text-white/85 drop-shadow-lg group-hover:scale-110 transition-transform duration-500" /></div>
                          <div className="absolute bottom-0 left-0 right-0 p-3">
                            <div className="text-[9px] text-white/50 uppercase tracking-wide">Provider — Demo</div>
                            <div className="font-display font-600 text-white text-sm leading-tight">{s.title}</div>
                          </div>
                          <div className="absolute inset-0 grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40">
                            <span className="btn-neon rounded-full h-10 w-10 grid place-items-center"><Icons.Play className="h-4 w-4 fill-white" /></span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Mid-mission decision */}
              <div className="space-y-4">
                <Ghost><span className="not-italic font-700 text-[#FF204E] tracking-wide">ALARM TRIGGERED.</span> Security is locking down the building.</Ghost>
                <div className="grid sm:grid-cols-2 gap-4">
                  <button onClick={() => setMid('push')} className={`xc-card rounded-2xl p-6 text-left ${mid === 'push' ? 'border-[#FF204E]/60' : ''}`}>
                    <AlertTriangle className="h-6 w-6 text-[#FF204E] mb-3" />
                    <h3 className="font-display font-700 text-white">PUSH THROUGH</h3>
                    <ul className="mt-2 text-sm text-white/60 space-y-1"><li>↑ Higher difficulty</li><li className="text-[#E0B85A]">↑ Higher reward</li></ul>
                    {mid === 'push' && <div className="mt-3 text-xs text-[#FF204E] flex items-center gap-1"><Check className="h-3.5 w-3.5" /> {chooseNoun}</div>}
                  </button>
                  <button onClick={() => setMid('tunnel')} className={`xc-card rounded-2xl p-6 text-left ${mid === 'tunnel' ? 'border-[#B026FF]/60' : ''}`}>
                    <DoorOpen className="h-6 w-6 text-[#B026FF] mb-3" />
                    <h3 className="font-display font-700 text-white">TAKE THE TUNNEL</h3>
                    <ul className="mt-2 text-sm text-white/60 space-y-1"><li>↓ Lower difficulty</li><li>↓ Lower reward</li></ul>
                    {mid === 'tunnel' && <div className="mt-3 text-xs text-[#B026FF] flex items-center gap-1"><Check className="h-3.5 w-3.5" /> {chooseNoun}</div>}
                  </button>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button disabled={!mid} onClick={() => goResult(targetHit ? 'success' : 'success')} className="btn-neon rounded-xl px-7 py-3.5 font-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">COMPLETE MISSION <ArrowRight className="h-4 w-4" /></button>
                  <button disabled={!mid} onClick={() => goResult('failed')} className="btn-ghost rounded-xl px-6 py-3.5 font-500 disabled:opacity-50 disabled:cursor-not-allowed">Preview failure state</button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5 — RESULT */}
          {step === 5 && (
            <div className="xc-fade-up">
              <div className="relative rounded-3xl overflow-hidden">
                <img src={outcome === 'success' ? IMAGES.car : IMAGES.wetStreet} alt="" className="h-72 w-full object-cover" />
                <div className="absolute inset-0" style={{ background: outcome === 'success' ? 'linear-gradient(180deg,rgba(7,6,9,0.3),rgba(7,6,9,0.92))' : 'linear-gradient(180deg,rgba(7,6,9,0.4),rgba(7,6,9,0.95))' }} />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  {outcome === 'success' ? <Trophy className="h-12 w-12 text-[#E0B85A] mb-3" /> : <ShieldAlert className="h-12 w-12 text-[#FF204E] mb-3" />}
                  <h2 className="font-display font-700 text-4xl md:text-5xl text-white tracking-tight">{outcome === 'success' ? 'MISSION SUCCESSFUL' : 'MISSION FAILED'}</h2>
                  <p className="mt-3 text-white/70 max-w-lg">{outcome === 'success' ? cfg.successMsg : (solo ? 'The alarm was triggered. You slipped into the rain, but the job is blown for tonight.' : 'The alarm was triggered. The crew escaped, but the bank is now on high alert.')}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                <div className="xc-glass rounded-2xl p-5"><div className="text-xs text-white/40 uppercase tracking-wide">Loyalty Coins</div><div className="font-display font-700 text-2xl neon-gold mt-1">+{(outcome === 'success' ? cfg.rewardCoins.success : cfg.rewardCoins.fail).toLocaleString()} LC</div></div>
                <div className="xc-glass rounded-2xl p-5"><div className="text-xs text-white/40 uppercase tracking-wide">Crew Reputation</div><div className="font-display font-700 text-2xl text-[#B026FF] mt-1">+{outcome === 'success' ? cfg.rewardRep.success : cfg.rewardRep.fail}</div></div>
                {outcome === 'success' && <div className="xc-glass rounded-2xl p-5 border border-[#E0B85A]/30"><div className="text-xs text-white/40 uppercase tracking-wide">Unlocked</div><div className="font-display font-700 text-lg neon-gold mt-1 flex items-center gap-2"><Car className="h-5 w-5" /> {cfg.unlockLabel}</div></div>}
              </div>

              {outcome !== 'success' && <p className="mt-4 text-xs text-white/40">Failure is simply part of the narrative. There is never any suggestion to deposit, keep spinning or wager more.</p>}

              <div className="mt-7 flex flex-wrap gap-3">
                <button onClick={() => nav('/profile')} className="btn-gold rounded-xl px-7 py-3.5 font-700 flex items-center gap-2">CONTINUE <ArrowRight className="h-4 w-4" /></button>
                <button onClick={() => nav('/casino')} className="btn-ghost rounded-xl px-6 py-3.5 font-500">Back to Casino</button>
                <button onClick={() => setOutcome(outcome === 'success' ? 'failed' : 'success')} className="btn-ghost rounded-xl px-6 py-3.5 font-500">Toggle {outcome === 'success' ? 'failure' : 'success'} view</button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <AlertDialogContent className="bg-[#151119] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Leave this mission?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">{solo ? 'There is no penalty. You can pick this job back up any time.' : 'There is no penalty. Your crew will simply see “Crew Slot Available” — never that you abandoned them.'}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Stay</AlertDialogCancel>
            <AlertDialogAction onClick={leave} className="bg-[#D90035] hover:bg-[#FF204E] text-white border-0">Leave Mission</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
